/**
 * Client-friendly question templates for each cross-check and transaction check.
 *
 * Maps check ID → a question phrased for the client, not the CPA.
 * Used by AddRequestModal to pre-fill the question text when the CPA
 * clicks "Add to Client Requests" on a check.
 *
 * If a check ID is not in this map, the modal falls back to
 * a generic question derived from the check label.
 */

const CLIENT_QUESTION_TEMPLATES = {
	// ── Capital Assets ──────────────────────────────────────────────────
	CA_01: "Can you provide the depreciation schedule for your capital assets? We need to confirm accumulated depreciation.",
	CA_02: "Can you confirm whether depreciation was recorded for the current year? Please provide the depreciation schedule if available.",
	CA_03: "We see depreciation expense recorded but no capital assets on file. Can you clarify what assets this depreciation relates to?",
	CA_04: "We see accumulated depreciation but no capital assets. Were any assets disposed of this year? Please provide details.",
	CA_05: "The accumulated depreciation exceeds the cost of your capital assets. Can you provide the asset continuity schedule so we can review?",
	CA_06: "Depreciation expense was recorded, but we only see land (which is not depreciable). Can you clarify which assets the depreciation relates to?",

	// ── Intangibles ──────────────────────────────────────────────────────
	INT_01: "Can you provide the amortization schedule for your intangible assets?",
	INT_02: "We see amortization expense but no intangible assets on file. Can you clarify what this amortization relates to?",
	INT_03: "The accumulated amortization exceeds the cost of your intangible assets. Can you provide the schedule so we can review?",

	// ── Inventory ────────────────────────────────────────────────────────
	INV_01: "You have inventory on the balance sheet but no cost of goods sold. Can you confirm how inventory costs are being expensed?",
	INV_02: "Cost of goods sold was recorded but there is no inventory balance. Can you confirm whether you maintain inventory?",
	INV_03: "Your inventory balance appears to be negative. Can you provide the inventory count or valuation details?",

	// ── Balance Sheet ────────────────────────────────────────────────────
	BS_01: "We've identified a discrepancy in the balance sheet. Can you review your records and confirm all account balances are accurate?",

	// ── Receivables ──────────────────────────────────────────────────────
	AR_01: "You have trade receivables but no trade revenue recorded. Can you confirm the nature of these receivables?",
	AR_02: "You have trade revenue but no trade receivables. Are all customers paying immediately, or are there outstanding invoices at year end?",
	AR_03: "Your accounts receivable shows a credit balance. Can you clarify whether any customer overpayments or deposits should be reclassified?",

	// ── Equity ───────────────────────────────────────────────────────────
	EQ_01: "Can you provide the share capital details for the corporation (number of shares, classes, par value)?",
	EQ_02: "Total equity is negative, which may indicate a going concern issue. Can you provide information about the company's plans to continue operations?",
	EQ_03: "Share capital accounts appear on what is set up as a sole proprietorship or partnership. Can you confirm the entity type?",
	EQ_04: "Partner capital or owner drawings appear on a corporation. Can you confirm the entity type and how shareholder transactions are recorded?",
	EQ_05: "No retained earnings account is set up for the corporation. Can you confirm where prior-year earnings are being tracked?",

	// ── Deferred Revenue ─────────────────────────────────────────────────
	DR_01: "You have deferred revenue but no trade revenue. Can you confirm when this revenue will be earned?",
	DR_02: "Deferred revenue shows a debit (negative) balance. Can you clarify whether revenue was over-recognized or if there are refunds to process?",

	// ── Payables ──────────────────────────────────────────────────────────
	AP_01: "You have operating expenses but no trade payables at year end. Are all vendors paid before year end, or are there outstanding bills?",
	AP_02: "Accounts payable shows a debit (negative) balance. Can you confirm whether there are prepayments or overpayments to vendors?",

	// ── Debt ──────────────────────────────────────────────────────────────
	DEBT_01: "You have interest-bearing debt but no interest expense recorded. Can you provide the loan statements showing interest charges?",
	DEBT_02: "Interest expense was recorded but no loans or debt appear on the balance sheet. Can you clarify the source of this interest?",
	DEBT_03: "There is a current portion of long-term debt but no corresponding long-term debt. Can you provide the loan agreement or amortization schedule?",

	// ── Tax ───────────────────────────────────────────────────────────────
	TAX_01: "The company has positive net income but no corporate tax expense recorded. Can you confirm whether taxes have been provided for?",
	TAX_02: "Tax expense was recorded but the company has a net loss. Can you clarify the nature of this tax amount?",
	TAX_03: "There is a tax payable balance but no corresponding tax expense. Can you provide the tax assessment or installment details?",
	TAX_04: "You have trade revenue but no GST/HST payable or receivable. Can you confirm whether the business is registered for GST/HST?",

	// ── Prepaids ──────────────────────────────────────────────────────────
	PRE_01: "You have prepaid expenses but no corresponding operating expenses. Can you confirm what these prepaids relate to?",
	PRE_02: "A prepaid balance appears negative. Can you clarify whether an expense should be recorded or if there is a posting error?",

	// ── Retained Earnings / Cash / Dividends ─────────────────────────────
	RE_01: "The retained earnings balance does not reconcile from opening to closing. Can you provide the prior-year financial statements for comparison?",
	CASH_01: "The bank account shows a credit (negative) balance. Can you confirm whether this is an overdraft or if there are outstanding deposits?",
	DIV_01: "Dividends declared exceed available retained earnings. Can you confirm the dividend amounts and dates?",

	// ── Revenue / Completeness ────────────────────────────────────────────
	REV_01: "Revenue was recorded with no expenses. Can you confirm whether operating costs exist that should be classified?",
	COMP_01: "The financial statements appear to be missing key components. Can you confirm all accounts have been provided?",

	// ── Data Quality ─────────────────────────────────────────────────────
	DATA_01: "Some transactions are dated after the fiscal year end. Can you confirm whether these should be included in the current year?",

	// ── Shareholder ──────────────────────────────────────────────────────
	SH_01: "There is a shareholder loan receivable on the corporation. Can you confirm whether the loan was repaid within one year of the corporate year end, or if a prescribed-rate loan agreement is in place?",

	// ── Going Concern ────────────────────────────────────────────────────
	GC_01: "Working capital is negative. Can you provide information about available credit facilities or shareholder support to meet short-term obligations?",

	// ── GIFI ─────────────────────────────────────────────────────────────
	GIFI_01: "Some accounts are missing GIFI codes needed for the tax return. Can you provide the chart of accounts with GIFI assignments?",

	// ── Accrued Wages ────────────────────────────────────────────────────
	ACC_01: "Wage expense was recorded but no wages payable at year end. Can you confirm whether there are any unpaid wages or accrued payroll at the balance date?",

	// ── Transaction Analytical Checks ────────────────────────────────────
	TXN_INS_01: "Can you provide the insurance certificate showing the coverage period? We need to verify whether coverage spans the full fiscal year.",
	TXN_RENT_01: "Can you provide the lease agreement? We need to verify the rental period and confirm whether a prepaid or accrual is needed.",
	TXN_CAP_01: "We identified some larger expense items that may need to be capitalized as assets. Can you confirm whether these are one-time purchases or recurring expenses?",
	TXN_LEG_01: "Can you confirm that all legal fees are business-related? We need to ensure no personal legal matters were included.",
	TXN_MEAL_01: "Can you confirm the business purpose of the meals and entertainment expenses? CRA limits the deduction to 50%.",
	TXN_VEH_01: "What percentage of your vehicle use is for personal purposes? CRA requires this for the tax return.",
	TXN_WAGE_01: "Can you confirm that T4 summaries and payroll source deduction remittances are up to date?",
};

/**
 * Returns the client-friendly question for a check, or a generic fallback.
 */
function getClientQuestion(checkId, checkLabel, checkDetail) {
	if (CLIENT_QUESTION_TEMPLATES[checkId]) {
		return CLIENT_QUESTION_TEMPLATES[checkId];
	}
	// Generic fallback: clean up the label
	if (checkLabel) {
		let clean = checkLabel.split("—")[0].trim();
		return "Regarding: " + clean + " — can you provide clarification or supporting documentation?";
	}
	return "Can you provide clarification or supporting documentation for this item?";
}

export { CLIENT_QUESTION_TEMPLATES, getClientQuestion };
